import type { TestimonialSubmissionInput } from '../types';
import { element } from './dom';

export interface TestimonialSubmissionActions {
  readingSessionId: string;
  loadStatus: () => Promise<{ submitted: boolean }>;
  submit: (input: TestimonialSubmissionInput) => Promise<{ submitted: boolean; already_submitted?: boolean }>;
  onPromptViewed?: () => void;
  onSubmitted?: () => void;
}

function thankYou(section: HTMLElement): void {
  section.replaceChildren(
    element('span', { className: 'testimonial-share-glyph', text: '✦', attrs: { 'aria-hidden': 'true' } }),
    element('h2', { text: 'Thank You' }),
    element('p', { className: 'testimonial-share-thanks', text: 'Thank you. Your testimonial has been submitted for review.' }),
  );
}

function renderForm(section: HTMLElement, actions: TestimonialSubmissionActions): void {
  section.replaceChildren();
  section.append(
    element('span', { className: 'testimonial-share-glyph', text: '✦', attrs: { 'aria-hidden': 'true' } }),
    element('p', { className: 'result-kicker', text: 'YOUR EXPERIENCE' }),
    element('h2', { text: 'Share Your Experience', attrs: { id: 'testimonial-share-title' } }),
    element('p', { className: 'testimonial-share-intro', text: 'If your Alunar reading resonated with you, you’re welcome to share a short reflection for our review.' }),
  );
  const form = element('form', { className: 'testimonial-form' });
  const nameLabel = element('label');
  const name = element('input', { attrs: { type: 'text', name: 'display_name', minlength: '2', maxlength: '60', autocomplete: 'name', required: 'true' } });
  nameLabel.append(element('span', { text: 'Display name' }), name, element('small', { text: 'Use your first name and last initial, or another name you’re comfortable displaying publicly.' }));
  const textLabel = element('label');
  const testimonial = element('textarea', { attrs: { name: 'testimonial_text', minlength: '20', maxlength: '1200', rows: '6', required: 'true' } });
  textLabel.append(element('span', { text: 'Testimonial' }), testimonial, element('small', { text: '20–1,200 characters.' }));
  const ratingLabel = element('label');
  const rating = element('select', { attrs: { name: 'rating' } });
  ([['', 'No rating'], ['5', '5 — Excellent'], ['4', '4 — Very good'], ['3', '3 — Good'], ['2', '2 — Fair'], ['1', '1 — Poor']] as Array<[string,string]>).forEach(([value, label]) => rating.append(element('option', { text: label, attrs: { value } })));
  ratingLabel.append(element('span', { text: 'Rating (optional)' }), rating);
  const consentLabel = element('label', { className: 'testimonial-consent' });
  const consent = element('input', { attrs: { type: 'checkbox', name: 'consent', required: 'true' } });
  consentLabel.append(consent, element('span', { text: 'I agree that Alunar Astro may display this testimonial publicly using the display name shown above.' }));
  const submit = element('button', { className: 'secondary testimonial-submit', text: 'Submit for Review', attrs: { type: 'submit' } });
  const error = element('p', { className: 'error', attrs: { role: 'alert', 'aria-live': 'polite' } });
  form.append(nameLabel, textLabel, ratingLabel, consentLabel, submit, error);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submit.disabled) return;
    const displayName = name.value.trim();
    const testimonialText = testimonial.value.trim();
    if (displayName.length < 2 || testimonialText.length < 20 || !consent.checked) {
      error.textContent = !consent.checked ? 'Please confirm permission to display your testimonial.' : 'Please complete the required fields.';
      return;
    }
    submit.disabled = true;
    submit.textContent = 'Submitting…';
    error.textContent = '';
    const selectedRating = rating.value ? Number(rating.value) : null;
    void actions.submit({ reading_session_id: actions.readingSessionId, display_name: displayName, testimonial_text: testimonialText, rating: selectedRating, consent: true })
      .then(() => { actions.onSubmitted?.(); thankYou(section); })
      .catch((caught) => {
        submit.disabled = false;
        submit.textContent = 'Submit for Review';
        error.textContent = caught instanceof Error ? caught.message : 'Your testimonial could not be submitted. Please try again.';
      });
  });
  section.append(form);
  actions.onPromptViewed?.();
}

export function testimonialSubmissionView(actions: TestimonialSubmissionActions): HTMLElement {
  const section = element('section', { className: 'testimonial-share', attrs: { 'aria-labelledby': 'testimonial-share-title' } });
  section.append(element('p', { className: 'testimonial-share-loading', text: 'Checking your testimonial status…', attrs: { role: 'status' } }));
  void actions.loadStatus()
    .then((status) => status.submitted ? thankYou(section) : renderForm(section, actions))
    .catch(() => renderForm(section, actions));
  return section;
}
